// Recent uploads — the files a user dropped on the separator, kept locally so
// picking one up again is a click instead of another trip through the OS file
// dialog.
//
// Deliberately its OWN IndexedDB database, not a second store inside
// `riffloop-studio-stems`. Adding a store there would mean a version bump on a
// database every live session already has open, and a blocked upgrade would
// take the stem cache down with it. A separate database is purely additive:
// nothing that exists today can notice this file failing.
//
// The audio itself is never sent anywhere — this is the same browser-local
// storage the stem cache uses.

// One record per distinct file. Newest-first, capped by BOTH a count and a
// byte budget, because five 4 MB songs and five 48 MB ones are very different
// things to leave in someone's profile.
const DB_NAME = 'riffloop-studio-uploads';
const STORE = 'uploads';
// Three DISTINCT files. The byte budget is only a backstop: the picker caps an
// upload at 50 MB, so three of them fit under it and the count is what actually
// binds — you never silently get fewer than three.
const MAX_FILES = 3;
const MAX_BYTES = 160 * 1024 * 1024;

export interface RecentUpload {
  /** `name|size` — see uploadId. Re-picking the same file updates, never duplicates. */
  id: string;
  name: string;
  size: number;
  type: string;
  lastModified: number;
  /** Decoded length, so the row can read "3:24" without touching the blob. */
  duration: number;
  /** The 150-point envelope the picker already computed — free to keep, and it
   *  lets the row draw a real waveform instead of a seeded fake. */
  peaks: number[];
  blob: Blob;
  addedAt: number;
}

/** A recent upload without its audio — everything the rail needs to draw a row. */
export type RecentUploadMeta = Omit<RecentUpload, 'blob'>;

/**
 * What counts as "the same file". Name plus byte length, deliberately NOT
 * lastModified: re-downloading or re-copying a track gives it a fresh mtime,
 * which would file it as a second, identical-looking row. At five slots that
 * was untidy; at three it costs a third of the list. Two genuinely different
 * audio files sharing both a name and an exact byte length is not a case worth
 * reserving a slot against.
 */
export function uploadId(file: { name: string; size: number }): string {
  return `${file.name}|${file.size}`;
}

let _db: Promise<IDBDatabase> | null = null;
function open(): Promise<IDBDatabase> {
  if (_db) return _db;
  _db = new Promise<IDBDatabase>((resolve, reject) => {
    let req: IDBOpenDBRequest;
    try {
      req = indexedDB.open(DB_NAME, 1);
    } catch (e) {
      reject(e);
      return;
    }
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: 'id' });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
    // Another tab holding an older version open would otherwise hang this
    // promise forever, and it is cached — one blocked upgrade would kill the
    // feature for the rest of the session.
    req.onblocked = () => reject(new Error('upload cache blocked'));
  });
  // Every caller already treats a failure as "no recents"; make sure a rejected
  // open can be retried rather than poisoning the module for good.
  _db.catch(() => { _db = null; });
  return _db;
}

/**
 * Remember a file the user just loaded. Idempotent per file: re-picking one
 * refreshes its position instead of storing the bytes twice.
 */
export async function rememberUpload(
  file: File,
  meta: { duration: number; peaks: number[] },
): Promise<void> {
  // A single file over the whole budget would evict everything else and then
  // itself — cheaper to not store it than to churn the disk for nothing.
  if (file.size > MAX_BYTES) return;
  try {
    const db = await open();
    const rec: RecentUpload = {
      id: uploadId(file),
      name: file.name,
      size: file.size,
      type: file.type,
      lastModified: file.lastModified,
      duration: meta.duration,
      peaks: meta.peaks,
      blob: file,
      addedAt: Date.now(),
    };
    await new Promise<void>((resolve, reject) => {
      const t = db.transaction(STORE, 'readwrite');
      t.objectStore(STORE).put(rec);
      t.oncomplete = () => resolve();
      t.onerror = () => reject(t.error);
      t.onabort = () => reject(t.error);
    });
    await prune();
  } catch {
    /* best-effort: a quota refusal must not break the upload itself */
  }
}

/**
 * Every remembered upload, newest first, WITHOUT the audio. A cursor rather
 * than getAll() so listing the rail never pulls whole songs off disk.
 */
export async function listRecentUploads(): Promise<RecentUploadMeta[]> {
  try {
    const db = await open();
    return await new Promise<RecentUploadMeta[]>((resolve) => {
      const out: RecentUploadMeta[] = [];
      const req = db.transaction(STORE, 'readonly').objectStore(STORE).openCursor();
      req.onsuccess = () => {
        const cur = req.result;
        if (!cur) { resolve(out.sort((a, b) => b.addedAt - a.addedAt)); return; }
        const r = cur.value as RecentUpload;
        out.push({
          id: r.id, name: r.name, size: r.size, type: r.type,
          lastModified: r.lastModified, duration: r.duration,
          peaks: r.peaks || [], addedAt: r.addedAt || 0,
        });
        cur.continue();
      };
      req.onerror = () => resolve([]);
    });
  } catch {
    return [];
  }
}

/** The audio for one remembered upload, or null if it has since been evicted. */
export async function getUploadBlob(id: string): Promise<Blob | null> {
  try {
    const db = await open();
    return await new Promise<Blob | null>((resolve) => {
      const req = db.transaction(STORE, 'readonly').objectStore(STORE).get(id);
      req.onsuccess = () => resolve((req.result as RecentUpload | undefined)?.blob ?? null);
      req.onerror = () => resolve(null);
    });
  } catch {
    return null;
  }
}

export async function forgetUpload(id: string): Promise<void> {
  try {
    const db = await open();
    await new Promise<void>((resolve) => {
      const t = db.transaction(STORE, 'readwrite');
      t.objectStore(STORE).delete(id);
      t.oncomplete = () => resolve();
      t.onerror = () => resolve();
    });
  } catch {
    /* best-effort */
  }
}

/**
 * Collapse any duplicates of the same file, then drop whatever falls outside
 * the newest MAX_FILES or the byte budget.
 *
 * The dedupe pass is not redundant with the keyPath. Records written before
 * `uploadId` dropped lastModified carry the old three-part key, so the same
 * song can sit in the store twice under two different ids — and with only three
 * slots that stale twin would push out a real one. Matching on the signature
 * rather than the key cleans those up on the next write, newest kept.
 */
async function prune(): Promise<void> {
  const rows = await listRecentUploads(); // newest first
  const seen = new Set<string>();
  const doomed: string[] = [];
  let kept = 0;
  let bytes = 0;
  for (const r of rows) {
    const sig = uploadId(r);
    if (seen.has(sig)) { doomed.push(r.id); continue; } // an older copy of a file we already kept
    seen.add(sig);
    if (kept >= MAX_FILES || bytes + r.size > MAX_BYTES) { doomed.push(r.id); continue; }
    bytes += r.size;
    kept++;
  }
  for (const id of doomed) await forgetUpload(id);
}
